import React, { useEffect, useMemo, useState } from 'react';
import './index.less';
import { Tabs } from '@meitu/saas-ui';
import SketchPicker from '../color-sketch-picker';
import { SwatchPresetColor } from '../color-swatch';
import { useLocaleStore } from '../i18n';
import Gradient from './components/gradient-picker';
import Texture from './components/texture-picker';

export interface GradientPickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'color'> {
    prefixCls?: string;
    width?: number;
    color?: string;
    gradientDefaultColor?: string;
    presetColors?: SwatchPresetColor[];
    gradientPresetColors?: string[];
    editableDisable?: boolean;
    useRGBAInput?: boolean;
    onChange?: (params: { color: string; transparent: boolean; type?: string }) => void;
    onChangeComplete?: (newShade: any) => void;
    transparent?: boolean;
    isTexture?: boolean;
    textureData?: any;
    textureContent?: React.ReactNode;
    tabLock?: boolean;
}
enum TabsKye {
    PURE_COLOR = 1,
    GRADIENT = 2,
    TEXTURE = 3,
}
const GradientPicker = React.forwardRef<HTMLDivElement, GradientPickerProps>((props, ref) => {
    const {
        prefixCls = 'c-color-gradient-picker',
        className,
        onChange,
        onChangeComplete,
        width = 280,
        color,
        gradientDefaultColor,
        editableDisable = true,
        useRGBAInput = false,
        style,
        transparent = false,
        isTexture,
        textureContent,
        textureData,
        tabLock = false,
        ...other
    } = props;
    const { $t } = useLocaleStore();
    const [pureColor, setPureColor] = useState<{ color: string; transparent: boolean }>({
        color: '',
        transparent: false,
    });
    const [gradient, setGradient] = useState<string>('');
    const [activeIndex, setActiveIndex] = useState<number>(TabsKye.PURE_COLOR);
    useEffect(() => {
        if (textureData && textureData.url) {
            setActiveIndex(TabsKye.TEXTURE);
        } else if (typeof color === 'string' && color.includes('linear-gradient')) {
            setGradient(color);
            setActiveIndex(TabsKye.GRADIENT);
        } else {
            setPureColor({ color: color || '', transparent });
            setActiveIndex(TabsKye.PURE_COLOR);
        }
    }, []);
    useEffect(() => {
        if (activeIndex === TabsKye.PURE_COLOR) {
            if (pureColor.color) {
                onChange?.(pureColor);
                onChangeComplete?.(pureColor);
            }
        } else if (activeIndex === TabsKye.GRADIENT) {
            if (color !== gradient) {
                onChange?.({ color: gradient, transparent: false });
                onChangeComplete?.({ color: gradient, transparent: false });
            }
        } else {
            onChange?.({ type: 'texture', color: '', transparent: false });
        }
    }, [activeIndex]);

    const handleTabClick = (key) => {
        if (tabLock) return;
        if (key === TabsKye.PURE_COLOR) {
            !pureColor.color && setPureColor({ color: '#ffffff', transparent });
            setActiveIndex(TabsKye.PURE_COLOR);
        } else if (key === TabsKye.GRADIENT) {
            // 设置一个默认渐变
            !gradient && setGradient(gradientDefaultColor || 'linear-gradient(90deg, #ffffff 0%,#ff6261 100%)');
            setActiveIndex(TabsKye.GRADIENT);
        } else {
            setActiveIndex(TabsKye.TEXTURE);
        }
    };

    const tabsItems = [
        {
            name: $t((v) => v.Tab_SolidColor),
            tabKey: TabsKye.PURE_COLOR as unknown as string,
            content: pureColor.color ? (
                <SketchPicker
                    color={pureColor.color}
                    useRGBAInput={useRGBAInput}
                    transparent={pureColor.transparent}
                    onChange={(data) => {
                        setPureColor(data);
                        onChange && onChange(data);
                    }}
                    onChangeComplete={(data) => {
                        console.log('onChangeComplete', data);
                        onChangeComplete && onChangeComplete(data);
                    }}
                    presetColors={other.presetColors}
                />
            ) : null,
        },
        {
            name: $t((v) => v.Tab_Gradient),
            tabKey: TabsKye.GRADIENT as unknown as string,
            content: gradient ? (
                <Gradient
                    useRGBAInput={useRGBAInput}
                    gradient={gradient}
                    onChange={(data) => {
                        setGradient(data);
                        onChange && onChange({ color: data, transparent: false });
                    }}
                    onChangeComplete={(data) => {
                        console.log('onChangeComplete', data);
                        onChangeComplete && onChangeComplete({ color: data, transparent: false });
                    }}
                    presetColors={other.gradientPresetColors}
                />
            ) : null,
        },
        ...(isTexture
            ? [
                  {
                      name: $t((v) => v.Title_Patterns),
                      tabKey: TabsKye.TEXTURE as unknown as string,
                      content: <Texture textureContent={textureContent} />,
                  },
              ]
            : []),
    ];
    return (
        <div
            {...other}
            className={`${prefixCls} ${className || ''}`}
            ref={ref}
            style={{
                width,
                ...style,
            }}
        >
            <Tabs
                activeIndex={activeIndex}
                tabsWidth="248px"
                tabsHeight="28px"
                data={tabsItems}
                handleTabClick={handleTabClick}
            />
        </div>
    );
});

GradientPicker.displayName = 'GradientPicker';

export default GradientPicker;
